import { For, Portal, Show } from '@chakra-ui/react';
import type { DataTableActionMenuProps } from '@/components/Organisms/DataTable/DataTable.types';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { MenuPrimitives as CustomMenu } from '@/types/MenuPrimitives.types';
import { useEffect, useState } from 'react';

const DataTableActionMenu = ({
	item,
	actions = [],
	trigger,
	openOverride = false,
}: DataTableActionMenuProps) => {
	const menuBg = useColorModeValue('white', 'gray.700');
	const [isOpen, setIsOpen] = useState(false);

	const handleMouseEnter = () => {
		if (!openOverride) {
			setIsOpen(true);
		}
	};

	const handleMouseLeave = () => {
		if (!openOverride) {
			setIsOpen(false);
		}
	};

	// When openOverride is true, keep the menu open
	const effectiveIsOpen = openOverride || isOpen;

	// Automatically open the menu when openOverride becomes true
	useEffect(() => {
		if (openOverride && !isOpen) {
			setIsOpen(true);
		}
	}, [openOverride, isOpen]);

	return (
		<CustomMenu.Root 
			open={effectiveIsOpen} 
			onOpenChange={(e: { open: boolean }) => {
				if (!openOverride) {
					setIsOpen(e.open);
				}
			}}
		>
			<CustomMenu.Trigger
				outline="none"
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				style={{ cursor: 'pointer' }}
			>
				{trigger}
			</CustomMenu.Trigger>
			<Portal>
				<CustomMenu.Positioner>
					<CustomMenu.Content
						zIndex={1500}
						boxShadow={`0px 4px 4px 0px ${colors.menuListShadow}`}
						borderRadius={8}
						bg={menuBg}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						css={{
							// When menu opens downward (default)
							'&[data-placement^="bottom"]': {
								transform: 'translateY(-8px)',
							},
							// When menu opens upward
							'&[data-placement^="top"]': {
								transform: 'translateY(8px)',
							},
						} as React.CSSProperties}
					>
						<For each={actions}>
							{({ label, icon, actionFn, show }) => (
								<Show when={!show || show(item)}>
									<CustomMenu.Item
										key={label}
										px={3}
										py={1}
										cursor="pointer"
										onClick={() => actionFn(item)}
										borderRadius={4}
										_hover={{
											bg: 'brand.solidGradient',
											color: 'white',
										}}
									>
										{icon} {label}
									</CustomMenu.Item>
								</Show>
							)}
						</For>
					</CustomMenu.Content>
				</CustomMenu.Positioner>
			</Portal>
		</CustomMenu.Root>
	);
};

export default DataTableActionMenu;
